import type { InfiniteTableRowInfo } from "@infinite-table/infinite-react";
import type { NewsRaw } from "epsm-api-types/models";
import { nanoid } from "nanoid";
import type React from "react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { formatDateToFullTime } from "@/lib/utils/formatDateToFullTime/formatDateToFullTime";
import sprites from "/src/shared/assets/sprite.svg";
import { useGetRowDetailHeight } from "../../../lib/hooks/useGetRowDetailHeight";
import type { IBrief } from "../../../types";

type Props = {
	newsItem: InfiniteTableRowInfo<NewsRaw>;
	setRowDetailHeight: (v: number) => void;
	setExpandedRowDetailList: React.Dispatch<React.SetStateAction<number[]>>;
};

export const NewsBriefs: React.FC<Props> = (props) => {
	const { newsItem, setRowDetailHeight, setExpandedRowDetailList } = props;

	const preparedBriefs = useMemo(() => {
		return [...(newsItem?.data?.briefs ?? [])]?.filter(
			(item) => item.brief !== "No significant events",
		);
	}, [newsItem?.data?.briefs]);

	useGetRowDetailHeight<IBrief>(preparedBriefs, setRowDetailHeight);

	const summaryParts = useMemo(() => {
		return newsItem?.data?.summary?.split("\n").filter(Boolean) ?? [];
	}, [newsItem?.data?.summary]);

	if (!newsItem?.data) return null;

	const { significancy, publication_date } = newsItem.data;

	const date = publication_date && new Date(publication_date);
	const time = date && formatDateToFullTime(date);

	return (
		<div
			className="relevant h-full w-full flex gap-2 flex-col"
			data-testid="news-briefs"
		>
			<div className="flex gap-2 justify-between items-center">
				<div className="flex gap-2 fw font-bold">
					<p>{`${!!date && `${time}`}`}</p>
					<p className="whitespace-nowrap">
						{significancy && `Weight ${significancy}/10`}
					</p>
				</div>

				<Button
					className="h-max p-1 shadow-none group bg-transparent hover:bg-transparent cursor-pointer"
					onClick={() =>
						setExpandedRowDetailList((prev) =>
							prev?.filter((i) => i !== newsItem.indexInAll),
						)
					}
				>
					<svg
						aria-label="close"
						role="img"
						fill="none"
						stroke="var(--light-gray)"
						className="w-3.5 h-3.5 group-hover:stroke-accent-foreground"
					>
						<use xlinkHref={`${sprites}#close`} />
					</svg>
				</Button>
			</div>

			{summaryParts?.map((summaryPart) => {
				return <p key={`${summaryPart}-${nanoid()}`}>{summaryPart}</p>;
			})}

			<div className="flex flex-col gap-1 pb-1">
				{preparedBriefs.map((item) => (
					<div key={item.ticker} className="flex flex-col">
						<p className="text-gold font-bold">{`${item.ticker}:`}</p>
						{item?.brief
							?.split("\n")
							?.filter(Boolean)
							?.map((brief) => (
								<p key={`${brief}-${nanoid()}`} className="font-sans">
									{brief}
								</p>
							))}
					</div>
				))}
			</div>
		</div>
	);
};
